import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
} from '@angular/core';
import { debounceTime, takeUntil } from 'rxjs/operators';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { ControlValueAccessorComponent } from '../../../core';
import {
  APP_MODE,
  Appointment,
  BOOKING_TYPE,
  BusinessObject,
  CalendarAdditionalConfig,
  Customer,
  DEBOUNCE_TIME,
  DEFAULT_DURATION_IN_MINUTES,
  FIRST_AVAILABLE_STAFF_ID,
  FORBIDDEN_ACTION,
  Staff,
} from '../../../models';
import { LibCoreLayoutModule } from '../../core-layout';
import { ServiceSelectionFormComponent } from '../../service-selection-form';
import { StaffSelectionFormComponent } from '../../staff-selection-form';
import { BusinessObjectUtils, CustomerUtils } from '../../../utils';
import { CalendarAvailableSlotSelectionFormComponent } from '../calendar-available-slot-selection-form/calendar-available-slot-selection-form.component';
import { CalendarStorageService } from '../services/calendar-storage.service';
import { CustomerSelectionFormComponent } from '../../customer-selection-form/customer-selection-form.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { AbstractStorageService } from '../../../services';
import { MatCheckboxModule } from '@angular/material/checkbox';

@Component({
  selector: 'lib-calendar-appointment-form',
  templateUrl: './calendar-appointment-form.component.html',
  styleUrl: './calendar-appointment-form.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    LibCoreLayoutModule,
    ReactiveFormsModule,
    TranslateModule,
    CalendarAvailableSlotSelectionFormComponent,
    ServiceSelectionFormComponent,
    StaffSelectionFormComponent,
    CustomerSelectionFormComponent,
    // material design
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CalendarAppointmentFormComponent),
      multi: true,
    },
  ],
})
export class CalendarAppointmentFormComponent
  extends ControlValueAccessorComponent
  implements ControlValueAccessor
{
  public readonly FORM_NAME = {
    START_TIME: 'startTime',
    DURATION: 'estimatedDuration',
    NOTE: 'note',
    CUSTOMER_ID: 'customerId',
    STAFF_ID: 'staffId',
    STAFF_SELECTED_BY_CUSTOMER: 'staffSelected',
    PRODUCT_IDS: 'productIds',
  };

  // Normally CalendarAppointmentFormComponent should extend CoreCalendarEntryComponent,
  // but it extends already ControlValueAccessorComponent.
  // It's too complex to extend both classes -> set mode in calendarStorageService manually
  private _mode: APP_MODE;
  @Input() set mode(value: APP_MODE) {
    this._mode = value;
    this.updateFormValidators(value);
    this.calendarStorageService.setCalendarMode(value);
  }
  get mode(): APP_MODE {
    return this._mode;
  }

  private _calendarAdditionalConfig: CalendarAdditionalConfig;
  @Input() set calendarAdditionalConfig(value: CalendarAdditionalConfig) {
    this._calendarAdditionalConfig = value;
    this.calendarStorageService.setCalendarAdditionalConfig(value);
  }
  get calendarAdditionalConfig(): CalendarAdditionalConfig {
    return this._calendarAdditionalConfig;
  }

  @Input() customers: Customer[]; // required for non customer mode
  @Input() staffs: Staff[];
  @Input() businessObject: BusinessObject;

  @Input() set desiredStartTime(value: Date) {
    if (value && !this.controlStartTime.value) {
      this.controlStartTime.setValue(value);
    }
  }

  @Output() customersChange = new EventEmitter<Customer[]>();

  public originalAppointment: Appointment;

  public isRwgAppointment: boolean;
  public staffCannotServeAnyProduct = false;
  public staffCannotServeAllProduct = false;

  public isDisabled = false;
  public submitForm: FormGroup;

  constructor(
    private abstractStorageService: AbstractStorageService,
    private calendarStorageService: CalendarStorageService
  ) {
    super();

    this.submitForm = new FormGroup({
      [this.FORM_NAME.START_TIME]: new FormControl<Date>(null, [
        Validators.required,
      ]),
      [this.FORM_NAME.DURATION]: new FormControl<number>(
        this.getDefaultEstimatedDuration()
      ),
      [this.FORM_NAME.CUSTOMER_ID]: new FormControl<number>(null),
      [this.FORM_NAME.STAFF_ID]: new FormControl<number>(
        this.getDefaultStaffId()
      ),
      [this.FORM_NAME.STAFF_SELECTED_BY_CUSTOMER]: new FormControl<boolean>(
        false
      ),
      [this.FORM_NAME.PRODUCT_IDS]: new FormControl<number[]>(
        [],
        [Validators.required]
      ),
      [this.FORM_NAME.NOTE]: new FormControl<string>(null),
    });

    this.controlStaffId.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((selectedStaffId) => {
        this.updateStateOfStaffSelectedByCustomerCheckbox(selectedStaffId);

        this.updateAbilityToServeServiceOfStaff();
      });

    this.controlProductIds.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(() => {
        this.updateAbilityToServeServiceOfStaff();
      });

    this.submitForm.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(() => {
        if (this.submitForm.invalid) {
          this._onChange(null);
        } else {
          this._onChange(this.generateAppointmentToSubmit());
        }
      });

    this.calendarStorageService.calendarAdditionalConfig$
      .pipe(
        takeUntil(this.componentDestroyed),
        debounceTime(DEBOUNCE_TIME) // let wait for patchValue
      )
      .subscribe((calendarAdditionalConfig) => {
        if (
          calendarAdditionalConfig?.forbiddenActions?.includes(
            FORBIDDEN_ACTION.MANAGE_OTHERS_APPOINTMENTS
          )
        ) {
          this.controlStaffId.setValue(
            calendarAdditionalConfig?.primaryStaffId
          );
          this.controlStaffId.disable();
        }
      });
  }

  public updateEstimatedDuration(value: number): void {
    const valueToSave = value || this.getDefaultEstimatedDuration();
    this.controlDuration.setValue(valueToSave);
  }

  public shouldDisplayAdvanceForm(): boolean {
    return this.mode !== APP_MODE.BOOKING && this.mode !== APP_MODE.CHECKIN;
  }

  public getOriginalSelectedProductIds(): number[] {
    return this.originalAppointment?.services?.map((s) => s.productId);
  }

  public get controlStartTime(): AbstractControl {
    return this.submitForm.get(this.FORM_NAME.START_TIME);
  }

  public get controlDuration(): AbstractControl {
    return this.submitForm.get(this.FORM_NAME.DURATION);
  }

  public get controlProductIds(): AbstractControl {
    return this.submitForm.get(this.FORM_NAME.PRODUCT_IDS);
  }

  public get controlCustomerId(): AbstractControl {
    return this.submitForm.get(this.FORM_NAME.CUSTOMER_ID);
  }

  public get controlStaffId(): AbstractControl {
    return this.submitForm.get(this.FORM_NAME.STAFF_ID);
  }

  public get controlStaffSelectedByCustomer(): AbstractControl {
    return this.submitForm.get(this.FORM_NAME.STAFF_SELECTED_BY_CUSTOMER);
  }

  public get controlNote(): AbstractControl {
    return this.submitForm.get(this.FORM_NAME.NOTE);
  }

  private updateAbilityToServeServiceOfStaff(): void {
    const staffId = this.controlStaffId.value;
    const selectedProducts = BusinessObjectUtils.getListOfProducts(
      this.businessObject,
      this.controlProductIds.value
    );

    if (!selectedProducts?.length || !staffId) {
      this.staffCannotServeAnyProduct = false;
      this.staffCannotServeAllProduct = false;
      return;
    }

    const selectedProductsCanBeServedByStaff = selectedProducts?.filter((p) =>
      BusinessObjectUtils.isContainStaffIdAsSpecialist(p, staffId)
    );

    this.staffCannotServeAnyProduct =
      !selectedProductsCanBeServedByStaff?.length;
    this.staffCannotServeAllProduct =
      selectedProductsCanBeServedByStaff?.length &&
      selectedProductsCanBeServedByStaff?.length !== selectedProducts.length;
  }

  private generateAppointmentToSubmit(): Appointment {
    const appointmentToSubmit = CustomerUtils.generateAppointment(
      this.businessObject,
      this.staffs,
      this.controlStartTime.value,
      this.controlStaffId.value,
      this.controlProductIds.value
    );

    if (this.isRwgAppointment) {
      appointmentToSubmit.services = this.originalAppointment.services;
    }

    // if customer can edit appointment -> this check should be changed
    if (this.shouldDisplayAdvanceForm()) {
      appointmentToSubmit.note = this.controlNote.value || ' ';
      appointmentToSubmit.estimatedDuration = this.controlDuration.value;
      appointmentToSubmit.owner = this.customers?.find(
        (c) => c.id === this.controlCustomerId.value
      );
      appointmentToSubmit.staffSelected =
        this.controlStaffSelectedByCustomer.value;
    }

    return {
      ...(this.originalAppointment || {}),
      ...appointmentToSubmit,
    };
  }

  private patchSubmitForm(appointment: Appointment): void {
    const selectedCustomerId =
      appointment?.owner?.id ||
      this.getAnonymousCustomerId(this.businessObject, this.customers);

    // controlStartTime may contain desired start time
    const startTimeAsDate =
      appointment?.startTimeAsDate || this.controlStartTime.value;

    // if create new appointment -> auto select default staff Id
    const staffId = appointment?.id
      ? appointment?.staffId
      : appointment?.staffId || this.getDefaultStaffId();

    this.submitForm.patchValue({
      [this.FORM_NAME.CUSTOMER_ID]: selectedCustomerId,
      [this.FORM_NAME.STAFF_ID]: staffId,
      [this.FORM_NAME.STAFF_SELECTED_BY_CUSTOMER]: appointment?.staffSelected,
      [this.FORM_NAME.PRODUCT_IDS]: appointment?.services?.map(
        (s) => s.productId
      ),
      [this.FORM_NAME.START_TIME]: startTimeAsDate,
      [this.FORM_NAME.NOTE]: appointment?.note,
    });
  }

  private updateStateOfStaffSelectedByCustomerCheckbox(
    selectedStaffId: number
  ): void {
    this.controlStaffSelectedByCustomer.setValue(
      !!this.originalAppointment?.staffSelected &&
        !!this.originalAppointment?.staffId &&
        selectedStaffId === this.originalAppointment?.staffId
    );

    const isSpecificStaffSelected =
      !!selectedStaffId && selectedStaffId !== FIRST_AVAILABLE_STAFF_ID;
    if (isSpecificStaffSelected) {
      this.controlStaffSelectedByCustomer.enable();
    } else {
      this.controlStaffSelectedByCustomer.disable();
    }
  }

  private updateFormValidators(mode: APP_MODE): void {
    if (mode === APP_MODE.BOOKING || mode === APP_MODE.CHECKIN) {
      this.controlCustomerId.clearValidators();
      this.controlProductIds.setValidators([Validators.required]);
      this.controlStaffSelectedByCustomer.setValue(true);
    } else {
      this.controlCustomerId.setValidators([Validators.required]);
      this.controlProductIds.clearValidators();
    }

    this.submitForm.updateValueAndValidity();
  }

  private updateDisableState(): void {
    if (this.isDisabled) {
      this.submitForm.disable();
    } else {
      this.submitForm.enable();
      this.updateStateOfStaffSelectedByCustomerCheckbox(
        this.controlStaffId.value
      );
      if (
        this.calendarAdditionalConfig?.forbiddenActions?.includes(
          FORBIDDEN_ACTION.MANAGE_OTHERS_APPOINTMENTS
        )
      ) {
        this.controlStaffId.disable();
      }
    }

    if (this.originalAppointment?.id || this.isDisabled) {
      this.controlCustomerId.disable();
    } else {
      this.controlCustomerId.enable();
    }

    if (this.isRwgAppointment || this.isDisabled) {
      this.controlProductIds.disable();
    } else {
      this.controlProductIds.enable();
    }
  }

  private getAnonymousCustomerId(
    businessObject: BusinessObject,
    customers: Customer[]
  ): number {
    return customers?.find((c) =>
      CustomerUtils.isAnonymousCustomer(c, businessObject?.telephone)
    )?.id;
  }

  private getDefaultEstimatedDuration(): number {
    return (
      this.abstractStorageService.getLastSelectedDuration() ||
      DEFAULT_DURATION_IN_MINUTES
    );
  }

  private getDefaultStaffId(): number {
    if (this.mode === APP_MODE.STAFF) {
      return this.calendarAdditionalConfig?.primaryStaffId || null;
    }

    return null;
  }

  // required for NG_VALUE_ACCESSOR
  writeValue(appointment: Appointment) {
    this.originalAppointment = appointment || {};
    this.isRwgAppointment = appointment?.bookingType === BOOKING_TYPE.RWG;

    this.patchSubmitForm(appointment);
    this.updateDisableState();
  }

  // required for NG_VALUE_ACCESSOR
  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
    this.updateDisableState();
  }
}
